"use client"

import type React from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Card, CardBody, Table, Badge, Row, Col } from "reactstrap"
import { FaUser, FaEnvelope, FaPhone, FaIdCard } from "react-icons/fa"
import type { PrintOrder } from "./badge-request"

interface Props {
  isOpen: boolean
  toggle: () => void
  order: PrintOrder
}

const UserOrderDetailsModal: React.FC<Props> = ({ isOpen, toggle, order }) => {
  // Calculate total amount from order details
  const calculateTotalAmount = (order: PrintOrder): number => {
    if (!order.order_details) return 0
    return order.order_details.reduce((total, detail) => {
      return total + Number.parseFloat(detail.amount)
    }, 0)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "paid":
        return "info"
      case "approved":
        return "success"
      case "printed":
        return "primary"
      case "cancelled":
        return "danger"
      case "postpaid":
        return "secondary"
    }
  }

  const getStatusDescription = (status: string) => {
    switch (status) {
      case "pending":
        return "Your order is pending. Payment may be required."
      case "paid":
        return "Payment submitted. Waiting for verification."
      case "approved":
        return "Order approved. Badges will be printed soon."
    case "postpaid":
        return "Order approved and marked as postpaid. Badges will be printed soon."
      case "printed":
        return "Badges have been printed and are ready for collection."
      case "cancelled":
        return "Order has been cancelled."
      default:
        return ""
    }
  }

  const totalAmount = calculateTotalAmount(order)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Order Details - {order.order_number}</ModalHeader>
      <ModalBody>
        {/* Order Summary */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md={6}>
                <h6>Order Information</h6>
                <div className="mb-2">
                  <strong>Order Number:</strong> {order.order_number}
                </div>
                <div className="mb-2">
                  <strong>Status:</strong>{" "}
                  <Badge color={getStatusColor(order.status)} pill>
                    {order.status.toUpperCase()}
                  </Badge>
                </div>
                <div className="mb-2">
                  <small className="text-muted">{getStatusDescription(order.status)}</small>
                </div>
                <div className="mb-2">
                  <strong>Total Amount:</strong> RWF {totalAmount.toLocaleString()}
                  {totalAmount === 0 && (
                    <Badge color="success" className="ms-2">
                      FREE
                    </Badge>
                  )}
                </div>
                <div className="mb-2">
                  <strong>Created:</strong> {new Date(order.created_at).toLocaleString()}
                </div>
                {order.is_postpaid && <Badge color="info">Postpaid Account</Badge>}
                {/* ✅ Rejection Reason display */}
                {order.status === "cancelled" && order.reason && (
                  <div className="mb-2">
                    <strong className="text-danger">Rejection Reason:</strong>
                    <span className="ms-2">{order.reason}</span>
                  </div>
                )}
              </Col>

              
              <Col md={6}>
                <h6>Exhibition Information</h6>
                <div className="mb-2">
                  <strong>Exhibition:</strong> {order.participation_details?.exhibition_title}
                </div>
                <div className="mb-2">
                  <strong>Exhibitor:</strong> {order.participation_details?.exhibitor_name}
                </div>
                <div className="mb-2">
                  <strong>Stand Category:</strong> {order.participation_details?.stand_category_name}
                </div>
                <div className="mb-2">
                  <strong>Max Representatives:</strong> {order.participation_details?.num_representatives}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Payment Information */}
        {order.order_payments && order.order_payments.length > 0 && (
          <Card className="mb-4">
            <CardBody>
              <h6>Payment Information</h6>
              {order.order_payments.map((payment) => (
                <div key={payment.id} className="border rounded p-3 mb-2">
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Payment Method:</strong> {payment.payment_method.toUpperCase()}
                      </div>
                      <div className="mb-2">
                        <strong>Amount:</strong> RWF {payment.amount.toLocaleString()}
                      </div>
                      {/* <div className="mb-2">
                        <strong>Status:</strong>{" "}
                        <Badge color={payment.status === "completed" ? "success" : "warning"} pill>
                          {payment.status.toUpperCase()}
                        </Badge>
                      </div> */}
                    </Col>
                    <Col md={6}>
                      {payment.transaction_number && (
                        <div className="mb-2">
                          <strong>Transaction #:</strong> {payment.transaction_number}
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Submitted:</strong> {new Date(payment.created_at).toLocaleString()}
                      </div>
                      {payment.proof_of_payment && (
                        <div>
                          <strong>Proof:</strong>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL}${payment.proof_of_payment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Document
                          </a>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {/* Representatives List */}
        <Card>
          <CardBody>
            <h6>Representatives ({order.order_details?.length || 0})</h6>
            <div className="table-responsive">
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>ID Number</th>
                    <th>Role</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {order.order_details?.map((detail) => (
                    <tr key={detail.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaUser className="me-2 text-muted" size={12} />
                          {detail.representation.person.first_name} {detail.representation.person.last_name}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaEnvelope className="me-2 text-muted" size={12} />
                          {detail.representation.person.email}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaPhone className="me-2 text-muted" size={12} />
                          {detail.representation.person.phone}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaIdCard className="me-2 text-muted" size={12} />
                          {detail.representation.person.id_doc_number}
                        </div>
                      </td>
                      <td>{detail.representation.role || "N/A"}</td>
                      <td>
                        {Number.parseFloat(detail.amount) > 0 ? (
                          <Badge color="primary">RWF {Number.parseFloat(detail.amount).toLocaleString()}</Badge>
                        ) : (
                          <Badge color="success">FREE</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Status-specific messages */}
        {order.status === "pending" && totalAmount > 0 && !order.is_postpaid && (
          <Card className="mt-4 border-warning">
            <CardBody>
              <div className="d-flex align-items-center">
                <Badge color="warning" className="me-3">
                  ACTION REQUIRED
                </Badge>
                <div>
                  <strong>Payment Required</strong>
                  <div className="text-muted">
                    This order requires payment of RWF {totalAmount.toLocaleString()}. Please use the payment button to
                    complete your order.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {order.status === "paid" && (
          <Card className="mt-4 border-info">
            <CardBody>
              <div className="d-flex align-items-center">
                <Badge color="info" className="me-3">
                  UNDER REVIEW
                </Badge>
                <div>
                  <strong>Payment Under Verification</strong>
                  <div className="text-muted">
                    Your payment has been submitted and is being verified by our finance team. You will be notified once
                    approved.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {order.status === "approved" && (
          <Card className="mt-4 border-success">
            <CardBody>
              <div className="d-flex align-items-center">
                <Badge color="success" className="me-3">
                  APPROVED
                </Badge>
                <div>
                  <strong>Order Approved</strong>
                  <div className="text-muted">
                    Your order has been approved and badges are being prepared for printing.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {order.status === "printed" && (
          <Card className="mt-4 border-primary">
            <CardBody>
              <div className="d-flex align-items-center">
                <Badge color="primary" className="me-3">
                  READY
                </Badge>
                <div>
                  <strong>Badges Ready for Collection</strong>
                  <div className="text-muted">
                    Your badges have been printed and are ready for collection. Please contact the exhibition organizers
                    for pickup details.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default UserOrderDetailsModal
